import { observer } from "mobx-react";
import { compact, isEmpty, noop, sortBy } from "lodash";
import { TFunction, TMessage } from "@/i18n/tt";
import {
  brSignerRoleLabel,
  BundleSelectionDialogModel,
  ConfirmSendIncorporationToBRDialogModel,
  ConfirmSendToBRDialogModel,
  DecisionListItem,
  DialogState,
  enableSendToBRDate,
  getDecisionFollowUpPanels,
  getSendToBRSuccessMessage,
  getInterimBalanceSheetAltinnLink,
  getSigningStatusColor,
  getSigningStatusLabel,
  getViewingStatusColor,
  getViewingStatusLabel,
  PackageSigningDialogModel,
  RecipientModel,
  RecipientViewModel,
  ReopenLockedDocumentDialogModel,
  ReviewDocumentSigningDialogModel,
  ReviewSendNoticeOfBoardMeetingDialogModel,
  ReviewSendNoticeOfGeneralMeetingDialogModel,
  ReviewSendSubscriptionFormDialogModel,
  selectedBRSignerLabel,
  additionalBRSignerLines,
  AdditionalBRSignerLine,
  SendForReviewDialogModel,
  ViewDocumentSigningStatusDialogModel,
  ViewDocumentViewingStatusDialogModel,
  ViewSubscriptionFormBatchPreviewModel,
  ViewSubscriptionStatusDialogModel,
} from "./summaryDomain";
import {
  AttachmentFollowUpForm,
  IdentificationForPerson,
  ProvideIdentificationToPersonsForm,
} from "./SummaryController";
import { FC, Fragment, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import envelopeSending from "@/assets/img/envelope-sending.svg";
import envelopeSent from "@/assets/img/envelope-sent.svg";
import incorporationSendingReceipt from "@/dbot/projectList/incorporation-sending-receipt.svg";
import { Alert, Alert as AlertV2 } from "@/components-v2/Alert";
import { Autocomplete } from "@/components-v2/Autocomplete";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import {
  DetailsSummaryList,
  EditDetailsPanel,
  LocalDateTimePicker,
} from "@/dbot/common/commonComponents";
import {
  getSigningMethodLabel,
  isFailure,
  isOk,
  isPending,
  ProductName,
  Result,
  SigningMethod,
} from "@/common/common";
import {
  AdditionalBRSigner,
  AttachmentType,
  BRSigner,
  brSignerId,
  BRSignerRole,
  createMergerCounterpartEvent,
  DbotEvent,
  DbotEventDispatcher,
  defaultIncorporationActivationSubscription,
  DocumentItem,
  DocumentViewingStatus,
  dsePrekontrollErrorMessage,
  FollowUp,
  FollowUpDetailsPanel,
  FollowUpStatus,
  FollowUpType,
  getDecisionApprovedInBRDescription,
  getDecisionAttachmentsDescription,
  getDecisionAttachmentsEmailInformation,
  getDecisionAttachmentsEmailTitle,
  getDecisionName,
  getDecisionSendToBRDescription,
  getOpenNextPanelEvent,
  getOpenPrevPanelEvent,
  MeetingAuthMethod,
  parseDseSubmitAck,
  ProjectCompany,
  ProjectContext,
  SendToBRFollowUp,
  updateFollowUpStatusEvent,
} from "@/dbot/common/commonDomain";
import { Button, LinkButton } from "@/components-v2/Button";
import { SigningStatusForSigner, SubscriptionStatus } from "@/document/documentApi";
import {
  counterpartyOf,
  MergerDecisionType,
  openCounterpartProjectEvent,
} from "@/dbot/merger/mergerDomain";
import { RequireSubscriptionButton } from "@/dbot/projectList/projectListComponents";
import { requiresSubscription } from "@/payment/Payment";
import { Tag } from "@/components-v2/Tag";
import { TextWithTooltip } from "@/components-v2/TextWithTooltip";
import { Dialog } from "@/components-v2/Dialog";
import { MarkdownEditor } from "@/text/MarkdownEditor";
import { Box, Grid, GridItem, VBox } from "@/components-v2/Box";
import { Tabs } from "@/components-v2/Tabs";
import { Checkbox, MCheckboxGroup } from "@/components-v2/Checkbox";
import { getFullName } from "@/utils/Roles";
import { Icon } from "@/components-v2/Icon";
import { formatDateTime, formatWeekdayDateTime, relativeTime } from "@/i18n/dateFormat";
import { MTextInput } from "@/components-v2/TextInput";
import { Table } from "@/components-v2/Table";
import { SubscribersStatusDialog } from "@/dbot/increaseOfShareCapital/SubscribersStatus";
import { MRadioButtonGroup } from "@/components-v2/RadioButton";
import { AttachmentListItem, AttachmentsPanel } from "@/components-v2/AttachmentsPanel";
import {
  adminBoardArchive,
  adminDashboard,
  adminRoles,
  adminSettings,
  adminShareholderArchive,
  adminShareholders,
} from "@/utils/navigation";
import { MSelect, Select } from "@/components-v2/Select";
import { productNameLabel } from "@/enterpriseAccounts/enterpriseAccountsDomain";
import { isMobileView } from "@/utils/screen";
import { Markdown } from "@/text/Markdown";
import { when } from "@/utils/functions";
import { DecisionType } from "@/common/decisions";
import {
  useDispatch,
  useLanguage,
  useMessageStore,
  useTranslation,
  useUserStore,
} from "@/stores/RootStoreContext";
import { isProductionEnvironment } from "@/utils/environments";
import { BatchPreviewDialog } from "@/components-v2/BatchPreviewDialog";
import { BatchPreviewReady } from "@/components-v2/BatchPreviewDialog/BatchPreview";
import { ElectronicSubscriptionForm } from "@/views/Pages/SubscriptionFormSign/subscriptionFormComponents";
import {
  confirmAction,
  EventDispatcher,
  navigate,
  showPaymentDialog,
  thunk,
} from "@/events/eventsDomain";
import {
  ConfirmDeleteSigningRequestDialog,
  SendForSigningDialog,
  SendingTime,
} from "@/common/commonComponents";
import { RadioFieldModel, SignerModel, StringFieldModel } from "@/common/commonForms";
import { InfoButton } from "@/components-v2/InfoButton";
import { findShareTypeName, ShareType } from "@/common/share";
import { DocumentType, getDocumentLabel } from "@/common/documents";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { CompanyRole } from "@/types/CompanyInfo";
import { LocalizedTextEditor } from "@/text/LocalizedTextEditor";
import { ProjectController } from "@/dbot/project/projectDomain";
import { getActionIcon, getActionLabel } from "@/archive/commonArchiveDomain";
import { getMissingIdentificationDocumentations } from "@/dbot/ElectionOfCompanyBoard/electionOfCompanyBoardDomain";
import {
  CustomDocumentData,
  customDocumentTypeLabels,
} from "@/dbot/customDocument/customDocumentDomain";
import { logDebug } from "@/utils/log";
import {
  ActivatedIncorporationData,
  EventLog,
  EventType,
  extractActivatedIncorporation,
  extractRegisteredShareTransfer,
  extractBRSignerReminders,
  extractIsRegistration,
} from "@/common/eventLogs";
import { projectApi } from "../common/projectApi";

export const ReviewSendNoticeOfBoardMeetingDialog = observer(
  function ReviewSendNoticeOfBoardMeeting({
    model,
    dispatch,
  }: {
    model: ReviewSendNoticeOfBoardMeetingDialogModel;
    dispatch: EventDispatcher;
  }) {
    const { t } = useTranslation();
    const userStore = useUserStore();

    useEffect(() => {
      model.init(t);
    }, [model, t]);

    const subscriptionButton = (
      <RequireSubscriptionButton
        subscriptionProps={{
          ...model.subscriptionTypeModel,
          isLoading: model.isSending,
          isDisabled: !model.canSubmit,
          onNavigateToPayment: () => dispatch([showPaymentDialog]),
        }}
      />
    );
    return (
      <Dialog
        title={t(model.title)}
        submitLabel={t("send")}
        isSubmitting={model.isSending}
        isSubmitDisabled={!model.canSubmit}
        isCancelDisabled={!model.canClose}
        onSubmit={model.submit}
        onCancel={model.close}
        actions={requiresSubscription(model.subscriptionTypeModel) ? subscriptionButton : null}
      >
        <VBox gap={"lg"}>
          {!(model.isResend || model.isSendToNewParticipants) && (
            <Alert
              message={{ key: "dbot.summary.send-notice.participants-will-sync" }}
              type={"info"}
            />
          )}
          <LocalizedTextEditor model={model.emailBody} />
          {model.showAccessTypeAndRecipients && (
            <>
              <p>{t("dbot.meeting.email-link")}</p>
              <MeetingAuthenticationRadioGroup
                authMethod={model.authMethod}
                meetingPassword={model.meetingPassword}
                loginAlert={renderBoardMeetingLoginAlert(model, t, () =>
                  dispatch([navigate(adminRoles(userStore.orgNumber))]),
                )}
                isLocked={model.isSendToNewParticipants}
              />
              <VBox>
                <div>{t("dbot.recipients")}</div>
                <Grid cols={2} gap={"xs"}>
                  <div>{t("name")}</div>
                  <div>{t("email")}</div>
                  {model.recipients.recipientFields.map((item) => (
                    <Fragment key={item.id}>
                      <MTextInput model={item.nameField} disabled />
                      <MTextInput model={item.emailField} placeholder={t("email")} />
                    </Fragment>
                  ))}
                </Grid>
                {model.recipients.error && (
                  <Alert type={"error"} message={t(model.recipients.error)} />
                )}
              </VBox>
            </>
          )}
        </VBox>
      </Dialog>
    );
  },
);

const MeetingAuthenticationRadioGroup = observer(function MeetingAuthenticationRadioGroup({
  authMethod,
  meetingPassword,
  loginAlert,
  isLocked,
}: {
  authMethod: RadioFieldModel<MeetingAuthMethod | null>;
  meetingPassword: StringFieldModel;
  loginAlert?: ReactNode;
  isLocked?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <MRadioButtonGroup
        labelFn={(option) => {
          switch (option) {
            case null:
              return t("dbot.meeting.access-open");
            case "LOGIN":
              return t("dbot.meeting.access-bankid");
            default:
              return t("dbot.meeting.access-password");
          }
        }}
        model={authMethod}
        horizontal={!isMobileView()}
        disabled={isLocked}
      />
      {authMethod.value === null && <Alert message={t("dbot.meeting.direct-link")} type={"info"} />}
      {authMethod.value === "PASSWORD" && (
        <Grid cols={2} gap={"xs"}>
          <MTextInput
            model={meetingPassword}
            label={t("dbot.meeting.password-label")}
            disabled={isLocked}
          />
          <Box alignItems={"end"} padding={"md"}>
            <InfoButton infoText={t("dbot.meeting.direct-link-password.info")} />
          </Box>
        </Grid>
      )}
      {authMethod.value === "LOGIN" && loginAlert}
    </>
  );
});

function renderBoardMeetingLoginAlert(
  model: ReviewSendNoticeOfBoardMeetingDialogModel,
  t: TFunction,
  openCompanyRoles: () => void,
) {
  switch (true) {
    case model.hasExtraParticipants:
      return (
        <Alert
          message={{ key: "dbot.meeting.login.extra-participants" }}
          type={"error"}
          icon={"warning"}
        />
      );
    case !model.hasAllSocialSecurityNumbers:
      return (
        <Alert
          icon={"warning"}
          message={
            <VBox alignItems={"start"}>
              <Markdown value={t("dbot.meeting.login.missing-ssn")} enableLinks />
              <LinkButton
                label={t("dbot.meeting.open-company-roles")}
                onClick={openCompanyRoles}
                color={"inherit"}
              />
            </VBox>
          }
          type={"error"}
        />
      );
    default:
      return <Alert icon={"info"} message={{ key: "dbot.meeting.login.info" }} type={"info"} />;
  }
}

function renderGeneralMeetingLoginAlert(
  model: ReviewSendNoticeOfGeneralMeetingDialogModel,
  t: TFunction,
  openShareholders: () => void,
) {
  return (
    <>
      {model.params.isMissingShareholderSSN ? (
        <Alert
          icon={"warning"}
          message={
            <VBox alignItems={"start"}>
              <Markdown value={t("dbot.meeting.login.missing-ssn")} enableLinks />
              <LinkButton
                label={t("dbot.meeting.open-shareholders")}
                onClick={openShareholders}
                color={"inherit"}
              />
            </VBox>
          }
          type={"error"}
        />
      ) : (
        <Alert icon={"info"} message={{ key: "dbot.meeting.login.info" }} type={"info"} />
      )}
      {model.params.hasForeignShareholders && (
        <Alert message={{ key: "dbot.meeting.login.foreign-shareholders" }} type={"warning"} />
      )}
    </>
  );
}

export const ReviewSendNoticeOfGeneralMeetingDialog = observer(
  ({
    model,
    dispatch,
  }: {
    model: ReviewSendNoticeOfGeneralMeetingDialogModel;
    dispatch: EventDispatcher;
  }) => {
    const { t } = useTranslation();
    const userStore = useUserStore();

    useEffect(() => {
      model.init(t);
    }, [model, t]);

    const subscriptionButton = (
      <RequireSubscriptionButton
        subscriptionProps={{
          ...model.subscriptionTypeModel,
          isLoading: model.isSending,
          isDisabled: !model.canSubmit,
          onNavigateToPayment: () => {
            dispatch([showPaymentDialog]);
          },
        }}
      />
    );
    return (
      <Dialog
        title={t(model.title)}
        submitLabel={t("send")}
        isSubmitting={model.isSending}
        isSubmitDisabled={!model.canSubmit}
        isCancelDisabled={!model.canClose}
        onSubmit={model.submit}
        onCancel={model.close}
        actions={requiresSubscription(model.subscriptionTypeModel) ? subscriptionButton : null}
      >
        <VBox>
          {model.hasInvalidCompanyRoleRecipients ? (
            <Alert
              type={"error"}
              message={t("dbot.summary.noticeOfGeneralMeeting.hasInvalidRoles")}
            />
          ) : (
            <VBox gap={"lg"}>
              <LocalizedTextEditor model={model.emailBody} />
              <VBox>
                <p>{t("dbot.meeting.email-link")}</p>
                <MeetingAuthenticationRadioGroup
                  authMethod={model.authMethod}
                  meetingPassword={model.meetingPassword}
                  loginAlert={renderGeneralMeetingLoginAlert(model, t, () =>
                    dispatch([navigate(adminShareholders(userStore.orgNumber))]),
                  )}
                />
                <div>{t("dbot.recipients")}</div>
                <Grid cols={2} gap={"xs"}>
                  <div>{t("name")}</div>
                  <div>{t("email")}</div>
                  {model.recipients.recipientFields.map((item) => (
                    <Fragment key={item.id}>
                      <MTextInput model={item.nameField} disabled />
                      <MTextInput
                        model={item.emailField}
                        placeholder={t("email")}
                        onBlur={() => {
                          item.emailField.commit();
                          model.updateShareholderEmail(item);
                        }}
                      />
                    </Fragment>
                  ))}
                </Grid>
                {model.recipients.error && (
                  <Alert type={"error"} message={t(model.recipients.error)} />
                )}
                {isOk(model.updateEmailResult) && (
                  <Alert type={"success"} message={<>{t(model.updateEmailResult.data)}</>} />
                )}
                {isFailure(model.updateEmailResult) && (
                  <Alert type={"error"} message={<>{t(model.updateEmailResult.error)}</>} />
                )}
              </VBox>
            </VBox>
          )}
        </VBox>
      </Dialog>
    );
  },
);

export const ReviewSendSubscriptionFormDialog = observer(
  ({
    model,
    dispatch,
  }: {
    model: ReviewSendSubscriptionFormDialogModel;
    dispatch: EventDispatcher;
  }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    const subscriptionButton = (
      <RequireSubscriptionButton
        subscriptionProps={{
          ...model.subscriptionTypeModel,
          isLoading: model.isSending,
          isDisabled: !model.canSubmit,
          onNavigateToPayment: () => dispatch([showPaymentDialog]),
        }}
      />
    );
    return (
      <SendForSigningDialog
        form={model.form}
        title={model.title}
        isSending={model.isSending}
        canSubmit={model.canSubmit}
        canClose={model.canClose}
        close={model.close}
        submit={model.submit}
        submitButton={requiresSubscription(model.subscriptionTypeModel) ? subscriptionButton : null}
        information={
          model.form.isSignerListEmpty
            ? {
                message: t("dbot.summary.subscriptionForm.increaseNominalValueNotSupported"),
              }
            : undefined
        }
        commonSigningProperties={model.form.commonSigningProperties(locale)}
      />
    );
  },
);

export const SendForReviewDialog = observer(function SendForReviewDialog({
  model,
}: {
  model: SendForReviewDialogModel;
}) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const locale = useLanguage();

  useEffect(() => {
    model.init(t);
  }, [model, t]);

  useEffect(() => {
    if (scrollRef !== null && model.extraRecipients.length > 0) {
      scrollRef.current?.lastElementChild?.scrollIntoView();
    }
  }, [model.extraRecipients]);

  const columns: TableColumn<CompanyRole | RecipientModel>[] = compact([
    {
      name: "name",
      label: t("dbot.recipients"),
      align: "left",
      getValue: (recipient) =>
        model.isCompanyRole(recipient) ? (
          <Box>
            <Checkbox
              id={`message-recipient-${recipient.companyroleId}`}
              name={"message-recipient"}
              checked={!model.excludedRecipients.has(recipient.companyroleId)}
              onChange={noop} // handled by onRowClick in the DataTable below
            />
            <div>{getFullName(recipient)}</div>
          </Box>
        ) : (
          <MTextInput
            model={recipient.name}
            autofocus
            placeholder={t("name")}
            messageVariant="tooltip"
          />
        ),
      getSortValue: (row) => model.isCompanyRole(row) && getFullName(row),
    },
    {
      name: "role",
      label: t("roles"),
      align: "left",
      getValue: (recipient) =>
        model.isCompanyRole(recipient) ? (
          <div>{when(model.getRoleLabel(recipient), (label) => t(label))}</div>
        ) : (
          <div>{t("roles.external_recipient")}</div>
        ),
      getSortValue: (row) => model.isCompanyRole(row) && row.role,
    },
    {
      name: "email",
      label: t("email"),
      align: "left",
      getValue: (recipient) =>
        model.isCompanyRole(recipient) ? (
          <div>{recipient.email}</div>
        ) : (
          <MTextInput model={recipient.email} placeholder={t("email")} messageVariant="tooltip" />
        ),
      getSortValue: (row) => model.isCompanyRole(row) && row.email,
    },
  ]);

  return (
    <Dialog
      title={t("dbot.send-for-review")}
      onCancel={model.cancel}
      onSubmit={() => model.submit(locale)}
      submitLabel={t("send")}
      isSubmitting={model.isSending}
      actions={
        <Box
          gap={"md"}
          justifyContent={"space-between"}
          alignItems={"center"}
          vertical={isMobileView()}
        >
          {model.view === "recipients" ? (
            <LinkButton
              label={t("documents.noticeBoardMeeting.addRecipient")}
              icon={"plus"}
              onClick={model.addRecipient}
            />
          ) : (
            <span />
          )}
          <Box>
            <Button
              label={t("cancel")}
              onClick={model.cancel}
              disabled={model.isSending}
              variant="secondary"
            />
            <Button
              variant={"primary"}
              label={t("send")}
              onClick={() => model.submit(locale)}
              loading={model.isSending}
              disabled={model.canSubmit}
            />
          </Box>
        </Box>
      }
    >
      <VBox>
        <Tabs
          scrollable={false}
          tabs={model.viewOptions.map((v) => ({
            id: v,
            label: t(model.viewLabel(v)),
            isActive: v === model.view,
            onSelect: () => model.setView(v),
          }))}
        />
        {model.error && <Alert message={t(model.error)} type={"error"} />}
        {model.view === "options" && (
          <VBox marginX marginTop gap={"md"}>
            <Box alignItems={"center"}>
              <Checkbox
                label={t(model.votingOnDecisionMatterLabel)}
                checked={model.enabledVotingOnDecisionMatters}
                onChange={model.toggleEnableVotingOnDecisionMatter}
                disabled={model.disableVotingOnMatter}
              />
              <InfoButton infoText={model.infoButtonMessage()} />
            </Box>
            <Box alignItems="center">
              <Checkbox
                label={t("set-deadline")}
                checked={model.deadlineForFeedback}
                onChange={model.toggleDeadlineForFeedback}
              />
              {model.deadlineForFeedback && (
                <LocalDateTimePicker
                  dateField={model.deadlineDate}
                  timeField={model.deadlineTime}
                  onDateChange={() => model.updateDeadlineFeedbackDate(t)}
                  onTimeFieldBlur={() => model.updateDeadlineFeedbackDate(t)}
                  messageVariant="tooltip"
                />
              )}
            </Box>
            <div className={"h-100"}>
              <MarkdownEditor
                label={t("customize-message")}
                size={"full"}
                idleTimeout={20000}
                value={model.body}
                onChange={(value) => model.setBody(value, t)}
              />
            </div>
          </VBox>
        )}
        {model.view === "recipients" && (
          <VBox paddingRight={"md"}>
            <Box marginTop={"sm"} marginBottom={"sm"}>
              <LinkButton
                label={model.isAllSelected ? t("select-none") : t("select-all")}
                onClick={model.toggleAllRecipients}
              />
            </Box>
            <DataTable
              containerClassName={"max-h-108"}
              hoverable
              tableColumns={columns}
              onRowClick={(row) => {
                if (model.isCompanyRole(row)) {
                  model.toggleRecipient(row.companyroleId);
                }
              }}
              tableData={[...model.recipients, ...model.extraRecipients]}
              getId={(row) => model.getId(row)}
              rowActions={(row) =>
                compact([
                  model.canDeleteRecipient(row) && [
                    "icon",
                    {
                      icon: "trash",
                      tooltip: { key: "delete" },
                      onClick: () => model.deleteRecipient(row),
                      color: "error",
                    },
                  ],
                ])
              }
            />
            {model.extraRecipients.length > 0 && (
              <>
                <Alert type="info" message={t("only-board-members-can-vote")} />
                <div ref={scrollRef} />
              </>
            )}
          </VBox>
        )}
      </VBox>
    </Dialog>
  );
});

export const ReviewDocumentSigningDialog = observer(function ReviewDocumentSigning({
  model,
  dispatch,
}: {
  model: ReviewDocumentSigningDialogModel;
  dispatch: EventDispatcher;
}) {
  const { t } = useTranslation();
  const locale = useLanguage();
  const subscriptionButton = (
    <RequireSubscriptionButton
      subscriptionProps={{
        ...model.subscriptionTypeModel,
        isLoading: model.isSending,
        isDisabled: !model.canSubmit,
        onNavigateToPayment: () => dispatch([showPaymentDialog]),
      }}
    />
  );

  return (
    <SendForSigningDialog
      form={model.form}
      title={model.title}
      isSending={model.isSending}
      canSubmit={model.canSubmit}
      canClose={model.canClose}
      close={model.close}
      submit={model.submit}
      submitButton={requiresSubscription(model.subscriptionTypeModel) ? subscriptionButton : null}
      information={
        model.infoMessage
          ? { message: t(model.infoMessage) }
          : model.form.isSubscriptionSignersPresent
            ? {
                icon: "certificate",
                message: (
                  <span>
                    <strong>{t("signing.isSubscriptionSignersPresent")}</strong>{" "}
                    {t("signing.isSubscriptionSignersPresent.description")}
                  </span>
                ),
              }
            : undefined
      }
      showUnsupportedAttachmentWarning={model.showUnsupportedAttachmentWarning}
      destinationFolderLabel={
        model.params.document.type === DocumentType.board_meeting
          ? "archive.board-archive"
          : model.params.document.type === DocumentType.general_meeting
            ? "archive.shareholder-archive"
            : undefined
      }
      signingCompleteInfoMessage={
        model.params.document.type === DocumentType.board_meeting ? (
          <div>
            {t("send-for-signing.board-meeting-minutes.saved-to-archive.message-start")}
            <a
              rel="noopener noreferrer"
              target={"_blank"}
              href={adminBoardArchive(model.params.orgNo)}
              className={"text-inherit"}
            >
              {t("send-for-signing.board-meeting-minutes.saved-to-archive.link")}
            </a>
            {t("send-for-signing.board-meeting-minutes.saved-to-archive.message-end")}
          </div>
        ) : model.params.document.type === DocumentType.general_meeting ? (
          <div>
            {t("send-for-signing.general-meeting-minutes.saved-to-archive.message-start")}
            <a
              rel="noopener noreferrer"
              target={"_blank"}
              href={adminShareholderArchive(model.params.orgNo)}
              className={"text-inherit"}
            >
              {t("send-for-signing.general-meeting-minutes.saved-to-archive.link")}
            </a>
            {t("send-for-signing.general-meeting-minutes.saved-to-archive.message-end")}
          </div>
        ) : undefined
      }
      showMessageFields
      commonSigningProperties={model.form.commonSigningProperties(locale)}
      enableFullscreenMode
      documentsForSigning={model.documentsForSigning}
      companiesForSigning={model.companiesForSigning}
      preview={model.documentPreview({ t })}
    />
  );
});

const SignerTag: FC<{ name: string; dimmed?: boolean }> = ({ name, dimmed }) => (
  <Box
    border
    rounded
    paddingX={"sm"}
    paddingY={"xs"}
    gap={"xs"}
    alignItems={"center"}
    fg={dimmed ? "subtle" : undefined}
  >
    <Icon name={"signature"} />
    {name}
  </Box>
);

export const BundleSelectionDialog = observer(function BundleSelectionDialogComponent({
  model,
}: {
  model: BundleSelectionDialogModel;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      title={{ key: "dbot.send-together.title" }}
      submitLabel={
        model.selectedCount === 0
          ? t("dbot.send-together.continue-none")
          : t("dbot.send-together.continue", { count: model.selectedCount })
      }
      isSubmitDisabled={model.selectedCount === 0}
      onSubmit={() => model.submit()}
      onCancel={() => model.close()}
    >
      <VBox gap={"lg"}>
        <div>{t(model.descriptionMessage)}</div>
        <VBox gap={"md"}>
          <b>{t("dbot.send-together.select-documents")}</b>
          <VBox gap={"xl"}>
            {model.documents.map((doc) => (
              <VBox key={doc.documentId} gap={"sm"}>
                <Checkbox
                  label={{ translated: doc.title }}
                  checked={model.isChecked(doc.documentId)}
                  onChange={() => model.toggle(doc.documentId)}
                />
                <Box gap={"sm"} wrap>
                  {model.sortedSigners(doc).map((signer, index) => (
                    <SignerTag
                      key={`${doc.documentId}-${index}`}
                      name={signer.name}
                      dimmed={!model.isMultiDocumentSigner(signer)}
                    />
                  ))}
                </Box>
              </VBox>
            ))}
          </VBox>
        </VBox>
      </VBox>
    </Dialog>
  );
});

export const PackageSigningDialog = observer(function PackageSigning({
  model,
  dispatch,
}: {
  model: PackageSigningDialogModel;
  dispatch: EventDispatcher;
}) {
  const locale = useLanguage();
  const subscriptionButton = (
    <RequireSubscriptionButton
      subscriptionProps={{
        ...model.subscriptionTypeModel,
        isLoading: model.isSending,
        isDisabled: !model.canSubmit,
        onNavigateToPayment: () => dispatch([showPaymentDialog]),
      }}
    />
  );

  return (
    <SendForSigningDialog
      form={model.form}
      title={model.title}
      headerTitle={model.title}
      isSending={model.isSending}
      canSubmit={model.canSubmit}
      canClose={model.canClose}
      close={model.close}
      submit={model.submit}
      submitButton={requiresSubscription(model.subscriptionTypeModel) ? subscriptionButton : null}
      showUnsupportedAttachmentWarning={model.showUnsupportedAttachmentWarning}
      showMessageFields
      commonSigningProperties={model.form.commonSigningProperties(locale)}
      enableFullscreenMode
      documentsForSigning={model.documentsForSigning}
      documentDestinations={model.documentDestinations}
      preview={model.documentPreview()}
    />
  );
});

const SigningStatusTableEditRow: FC<{
  model: ViewDocumentSigningStatusDialogModel;
  signer: SignerModel;
}> = observer(({ model, signer }) => {
  const { t } = useTranslation();

  return (
    <tr>
      <td>{signer.nameField.inputValue}</td>
      <td>
        <MTextInput model={signer.emailField} placeholder={t("email")} variant={"in-table"} />
      </td>
      <td>
        <MSelect name={"language"} model={signer.languageField} />
      </td>
      <td colSpan={2}>
        <MSelect name={"signingMethod"} model={signer.methodField} />
      </td>
      <td>
        <Box gap={"md"}>
          {model.showCancelEditButton() && (
            <LinkButton label={t("cancel")} onClick={model.cancelEdit} />
          )}
          <LinkButton
            icon={"paper-plane"}
            label={t("send")}
            loading={model.isSendingEditedSigner()}
            disabled={model.isSendingEditedSigner()}
            onClick={model.sendEditedSigner}
          />
        </Box>
      </td>
    </tr>
  );
});

const SigningStatusTableRow: FC<{
  model: ViewDocumentSigningStatusDialogModel;
  signer: SigningStatusForSigner;
}> = observer(({ model, signer }) => {
  const { t } = useTranslation();
  return (
    <tr>
      <td>
        <VBox gap={false}>
          <div>{signer.name}</div>
          <SendingTime sendingTime={signer.sendingTime} />
        </VBox>
      </td>
      <td>{signer.email}</td>
      <td>{signer.language ? t(`language.${signer.language}`) : ""}</td>
      <td>{t(getSigningMethodLabel(signer.signingMethod))}</td>
      <td>
        <Tag color={getSigningStatusColor(signer)} label={t(getSigningStatusLabel(signer))} />
      </td>
      <td>
        <Box gap={"md"} justifyContent={"end"}>
          {model.showEditButton(signer) && (
            <LinkButton
              label={t("edit")}
              disabled={model.isEditDisabled()}
              onClick={() => model.edit(signer)}
            />
          )}
          {model.showResendButton(signer) && (
            <LinkButton
              label={t("dbot.summary.resend")}
              loading={model.isResending(signer)}
              disabled={model.isResendDisabled(signer)}
              onClick={() => model.resend(signer)}
            />
          )}
          {model.showCopyLinkButton && signer.signingUrl && (
            <LinkButton
              icon={"link"}
              label={t("signing.link")}
              onClick={() => model.copySigningLinkToClipboard(signer)}
              tooltip={t(model.tooltipLabel(signer))}
              disabled={signer.signingMethod === SigningMethod.HANDWRITING}
            />
          )}
        </Box>
      </td>
    </tr>
  );
});

const SigningStatusTable: FC<{
  model: ViewDocumentSigningStatusDialogModel;
}> = observer(({ model }) => {
  const { t } = useTranslation();
  return (
    <Table variant={"IN_CARD_DENSE"} withRowBorders>
      {model.groupedSigners.map(([companyName, signers], i) => (
        <Fragment key={companyName}>
          <thead>
            {model.showGroupHeading(companyName) && (
              <tr>
                <th align={"left"}>
                  <Box
                    fontWeight={"bold"}
                    textSize={"normal"}
                    fg={"default"}
                    marginTop={i > 0}
                    marginBottom={"sm"}
                  >
                    {companyName}
                  </Box>
                </th>
              </tr>
            )}
            <tr>
              <th align={"left"}>{t("name")}</th>
              <th align={"left"}>{t("email")}</th>
              <th align={"left"}>{t("documents.send.recipientEmailLanguage")}</th>
              <th align={"left"}>{t("signing.signingMethod")}</th>
              <th align={"left"}>{t("common.status")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {signers.map((s) =>
              model.state.type === "edit" && model.state.signedDocumentId === s.signedDocumentId ? (
                <SigningStatusTableEditRow
                  key={s.signedDocumentId}
                  model={model}
                  signer={model.state.model}
                />
              ) : (
                <SigningStatusTableRow key={s.signedDocumentId} model={model} signer={s} />
              ),
            )}
          </tbody>
        </Fragment>
      ))}
    </Table>
  );
});

export const ViewDocumentSigningStatusDialog = observer(
  ({ model }: { model: ViewDocumentSigningStatusDialogModel }) => {
    const { t } = useTranslation();

    function renderContent() {
      switch (model.documentSigningStatus.state) {
        case "pending":
          return <LoadingIndicator />;
        case "failure":
          return <Alert type={"error"} message={t(model.documentSigningStatus.error)} />;
        case "ok":
          return (
            <VBox>
              {model.alertMessage && (
                <Alert
                  type={model.alertMessage.type}
                  message={t(model.alertMessage.message)}
                  onClose={model.clearAlertMessage}
                />
              )}
              <SigningStatusTable model={model} />
            </VBox>
          );
      }
    }

    return (
      <Dialog
        title={t(model.title)}
        maxWidth={"lg"}
        cancelLabel={t("close")}
        onCancel={model.showCloseButton ? model.close : undefined}
        isCancelDisabled={model.isCloseDisabled}
        actions={
          <Box
            justifyContent={
              model.confirmCancelSignatureRequest && model.hideCancelSignatureRequestButton
                ? "space-between"
                : "end"
            }
          >
            {model.confirmCancelSignatureRequest && model.hideCancelSignatureRequestButton && (
              <LinkButton
                label={getActionLabel("delete-signing-request")}
                icon={getActionIcon("delete-signing-request")}
                onClick={model.openConfirmDeleteSigningRequestDialog}
              />
            )}
            <Box>
              <Button
                label={t("close")}
                variant={"secondary"}
                onClick={model.showCloseButton ? model.close : undefined}
                disabled={model.isCloseDisabled}
              />
              <Button
                variant={"primary"}
                disabled={model.isSubmitDisabled}
                label={when(model.submitLabel, t)}
                loading={model.isSubmitting}
                onClick={model.showSubmitButton ? model.submit : undefined}
              />
            </Box>
          </Box>
        }
      >
        <>
          {renderContent()}
          {model.state.type === "delete-signature-request" &&
            model.confirmCancelSignatureRequest && (
              <ConfirmDeleteSigningRequestDialog
                isSubmitting={model.state.isLoading}
                onConfirm={model.confirmCancelSignatureRequest}
                onCancel={model.onClose}
              />
            )}
        </>
      </Dialog>
    );
  },
);

const SubscriptionStatusTableRow: FC<{
  subscription: SubscriptionStatus;
  shareType?: ShareType;
}> = observer(({ subscription, shareType }) => {
  const { lang } = useTranslation();
  const locale = useLanguage();
  return (
    <tr>
      <td align={"left"}>{subscription.name}</td>
      <td align={"left"}>{shareType ? findShareTypeName(shareType, lang) : ""}</td>
      <td align={"right"}>{subscription.availableShares}</td>
      <td align={"right"}>{subscription.isSigned ? subscription.numberOfShares : "-"}</td>
      <td align={"right"}>
        {subscription.isSigned ? formatDateTime(locale, subscription.signingTime) : ""}
      </td>
    </tr>
  );
});

const SubscriptionStatusTable: FC<{
  model: ViewSubscriptionStatusDialogModel;
}> = observer(({ model }) => {
  const { t } = useTranslation();
  return (
    <Table variant={"IN_CARD_DENSE"} withRowBorders>
      <thead>
        <tr>
          <th align={"left"}>{t("name")}</th>
          <th align={"left"}>{t("dbot.summary.subscription.shareType")}</th>
          <th align={"right"}>{t("dbot.summary.subscription.availableShares")}</th>
          <th align={"right"}>{t("dbot.summary.subscription.subscribed")}</th>
          <th align={"right"}>{t("dbot.summary.subscription.signatureDate")}</th>
        </tr>
      </thead>
      <tbody>
        {model.subscriptions.map((s) => (
          <SubscriptionStatusTableRow
            key={`${s.name}_${s.shareTypeId}`}
            subscription={s}
            shareType={model.getShareType(s.shareTypeId)}
          />
        ))}
      </tbody>
    </Table>
  );
});

export const ViewSubscriptionStatusDialog = observer(
  ({ model }: { model: ViewSubscriptionStatusDialogModel }) => {
    const { t } = useTranslation();

    function renderContent() {
      switch (model.subscriptionStatus.state) {
        case "pending":
          return <LoadingIndicator />;
        case "failure":
          return <Alert type={"error"} message={t(model.subscriptionStatus.error)} />;
        case "ok":
          return <SubscriptionStatusTable model={model} />;
      }
    }

    return (
      <Dialog
        title={t("dbot.summary.subscription")}
        cancelLabel={t("close")}
        submitLabel={when(model.submitLabel, t)}
        isSubmitting={model.isSubmitting}
        isSubmitDisabled={model.isSubmitDisabled}
        onSubmit={model.showSubmitButton ? model.submit : undefined}
        onCancel={model.showCloseButton ? model.close : undefined}
        maxWidth={"lg"}
        isCancelDisabled={model.isCloseDisabled}
      >
        {renderContent()}
      </Dialog>
    );
  },
);

const RecipientStatusTableEditRow: FC<{
  model: ViewDocumentViewingStatusDialogModel;
  recipient: RecipientViewModel;
}> = observer(({ model, recipient }) => {
  const { t } = useTranslation();
  return (
    <tr>
      <td>{recipient.name}</td>
      <td>
        <MTextInput model={recipient.emailField} placeholder={t("email")} />
      </td>
      <td></td>
      <td>
        <Box gap={"md"}>
          {model.showCancelEditButton() && (
            <LinkButton label={t("cancel")} onClick={model.cancelEdit} />
          )}
          <LinkButton
            label={t("send")}
            icon={"paper-plane"}
            loading={model.isSendingEditedRecipient()}
            disabled={model.isSendingEditedRecipient()}
            onClick={() => {
              model.sendUpdatedRecipient();
            }}
          />
        </Box>
      </td>
    </tr>
  );
});

const RecipientStatusTableViewRow: FC<{
  model: ViewDocumentViewingStatusDialogModel;
  recipient: DocumentViewingStatus;
}> = observer(({ model, recipient }) => {
  const { t } = useTranslation();

  return (
    <tr>
      <td>
        <VBox gap={false}>
          <div>{recipient.recipientName}</div>
          <SendingTime sendingTime={recipient.sendingTime} />
        </VBox>
      </td>
      <td>{recipient.recipientEmail}</td>
      <td>
        <Tag color={getViewingStatusColor(recipient)} label={t(getViewingStatusLabel(recipient))} />
      </td>
      <td>
        <Box gap={"md"}>
          <LinkButton
            label={t("edit")}
            disabled={model.isEditDisabled()}
            onClick={() => model.edit(recipient)}
          />
          <LinkButton
            label={t("dbot.summary.resend")}
            disabled={model.isResendDisabled()}
            loading={model.isResending(recipient)}
            onClick={() => model.resend(recipient)}
          />
        </Box>
      </td>
    </tr>
  );
});

const ViewDocumentSentStatusTable: FC<{
  model: ViewDocumentViewingStatusDialogModel;
}> = observer(({ model }) => {
  const { t } = useTranslation();
  return (
    <Table variant={"IN_CARD_DENSE"} withRowBorders>
      <thead>
        <tr>
          <th align={"left"}>{t("name")}</th>
          <th align={"left"}>{t("email")}</th>
          <th align={"left"}>{t("common.status")}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {model.recipientView.map((r) =>
          model.state.type === "edit" && model.state.viewDocumentId === r.recipientDocumentId ? (
            <RecipientStatusTableEditRow
              key={r.recipientDocumentId}
              model={model}
              recipient={model.state.model}
            />
          ) : (
            <RecipientStatusTableViewRow key={r.recipientDocumentId} model={model} recipient={r} />
          ),
        )}
      </tbody>
    </Table>
  );
});

export const ViewDocumentSentStatusDialog = observer(
  ({ model }: { model: ViewDocumentViewingStatusDialogModel }) => {
    const { t } = useTranslation();

    function renderContent() {
      switch (model.status.state) {
        case "pending":
          return <LoadingIndicator />;
        case "failure":
          return <Alert type={"error"} message={t(model.status.error)} />;
        case "ok":
          return (
            <VBox>
              {model.alertMessage && (
                <Alert
                  type={model.alertMessage.type}
                  message={t(model.alertMessage.message)}
                  onClose={model.clearAlertMessage}
                />
              )}
              <div className={"text-lg"}>{t("documents.document-are-sent-to")}</div>
              <ViewDocumentSentStatusTable model={model} />
            </VBox>
          );
      }
    }

    return (
      <Dialog
        title={t(model.title)}
        isSubmitting={model.isReloading}
        onCancel={model.close}
        actions={
          <Box justifyContent={"space-between"}>
            <LinkButton
              label={t("dbot.viewing.refresh")}
              icon={"arrows-clockwise"}
              onClick={model.showReloadButton ? model.reload : undefined}
              loading={model.isReloading}
            />
            <Button variant={"secondary"} label={t("close")} onClick={model.close} />
          </Box>
        }
      >
        {renderContent()}
      </Dialog>
    );
  },
);

function getMembers(item: DecisionListItem): IdentificationPopupInput {
  switch (item.decision.decisionData?.type) {
    case DecisionType.assignsNewCompanyBoard:
      return {
        items: compact([
          ...item.decision.decisionData.membersByName.map((member) => member.name),
          ...(item.decision.decisionData.deputyByName?.map((member) => member.name) ?? []),
        ]).map((d) => ({ name: d, identityNumber: "", email: "" })),
        title: { key: "dbot.requiredAttachments.BOARD_MEMBERS_ELECTION_IDENTIFICATIONS.title" },
        description: {
          key: "dbot.requiredAttachments.BOARD_MEMBERS_ELECTION_IDENTIFICATIONS.description",
        },
      };
    case DecisionType.employmentOfManagingDirector:
      return {
        items: Array.of({
          name: item.decision.decisionData.newManagingDirector ?? "",
          identityNumber: "",
          email: "",
        }),
        title: { key: "dbot.requiredAttachments.EMPLOY_CEO_IDENTIFICATION.title" },
        description: { key: "dbot.requiredAttachments.EMPLOY_CEO_IDENTIFICATION.description" },
      };
    case DecisionType.procura:
      return {
        items: item.decision.decisionData.procuraAssignedToByName
          .flatMap((d) => d.others)
          .map((d) => ({ name: d, identityNumber: "", email: "" })),
        title: { key: "dbot.requiredAttachments.PROCURA_BY_NAME_OTHERS_IDENTIFICATION.title" },
        description: {
          key: "dbot.requiredAttachments.PROCURA_BY_NAME_OTHERS_IDENTIFICATION.description",
        },
      };
    case DecisionType.electionOfCompanyBoard:
      return {
        items: getMissingIdentificationDocumentations(item.decision.decisionData.boardChanges),
        title: { key: "dbot.requiredAttachments.BOARD_MEMBERS_ELECTION_IDENTIFICATIONS.title" },
        description: {
          key: "dbot.requiredAttachments.BOARD_MEMBERS_ELECTION_IDENTIFICATIONS.description",
        },
      };
    default:
      return { items: [], title: { translated: "" }, description: { translated: "" } };
  }
}

type IdentificationPopupInput = {
  title: TMessage;
  description: TMessage;
  items: IdentificationForPerson[];
};

const ReopenLockedDocumentDialog: FC<{
  model: ReopenLockedDocumentDialogModel;
}> = observer(({ model }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t({
        key: model.canReopenDocument
          ? "reopen.dialog.header"
          : "reopen.dialog.header.not-available",
      })}
      submitLabel={t("reopen.dialog.submitLabel")}
      isSubmitting={model.isLoading()}
      onSubmit={model.canReopenDocument ? model.onSubmit : undefined}
      onCancel={model.onCancel}
    >
      {model.canReopenDocument ? (
        <>
          {isFailure(model.reopenResult) && (
            <AlertV2 type="error" message={t("dbot.action.reopen.error")} />
          )}
          <p>{t({ key: "reopen.dialog.description.1" })}</p>
          <p>{t({ key: "reopen.dialog.description.2" })}</p>
          {model.regeneratedDocuments.length > 0 && (
            <AlertV2
              icon
              type={"warning"}
              message={
                <VBox gap={"sm"}>
                  <div>{t("reopen.dialog.regenerated-documents")}</div>
                  <ul className={"m-0"}>
                    {model.regeneratedDocuments.map((title, i) => (
                      <li key={i}>{t(title)}</li>
                    ))}
                  </ul>
                </VBox>
              }
            />
          )}
        </>
      ) : (
        <Markdown value={t("reopen.dialog.not-available")} enableLinks />
      )}
    </Dialog>
  );
});

const IncorporationSendingReceiptDialog: FC<{
  onClose: () => void;
  confirmationDocumentCount: number;
}> = observer(({ onClose, confirmationDocumentCount }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={{ key: "decisions.incorporation.sending-receipt.title" }}
      onSubmit={onClose}
      submitLabel={{ key: "ok" }}
      maxWidth={"sm"}
    >
      <VBox gap={"lg"} alignItems={"center"} textAlign={"center"}>
        <img src={incorporationSendingReceipt} alt={""} width={240} />
        <VBox gap={"sm"}>
          <Box>{t("decisions.incorporation.sending-receipt.package-generated")}</Box>
          <Box>
            {t("decisions.incorporation.sending-receipt.next-step", {
              count: confirmationDocumentCount,
            })}
          </Box>
        </VBox>
      </VBox>
    </Dialog>
  );
});

export const SummaryDocumentDialog = observer(
  ({
    state,
    closeDialog,
  }: {
    state: DialogState;
    closeDialog: () => void;
    ctrl: ProjectController;
  }) => {
    const dispatch = useDispatch();
    switch (state.type) {
      case "reviewSendNoticeOfGeneralMeeting":
        return <ReviewSendNoticeOfGeneralMeetingDialog model={state.model} dispatch={dispatch} />;
      case "reviewSendNoticeOfBoardMeeting":
        return <ReviewSendNoticeOfBoardMeetingDialog model={state.model} dispatch={dispatch} />;
      case "reviewDocumentSigning":
        return <ReviewDocumentSigningDialog model={state.model} dispatch={dispatch} />;
      case "bundleSelection":
        return <BundleSelectionDialog model={state.model} />;
      case "packageSigning":
        return <PackageSigningDialog model={state.model} dispatch={dispatch} />;
      case "reviewSendSubscriptionForm":
        return <ReviewSendSubscriptionFormDialog model={state.model} dispatch={dispatch} />;
      case "viewDocumentSigningStatus":
        return <ViewDocumentSigningStatusDialog model={state.model} />;
      case "viewSubscriptionStatus":
        return <ViewSubscriptionStatusDialog model={state.model} />;
      case "viewIncreaseOfShareCapitalSubscribersStatus":
        return <SubscribersStatusDialog model={state.model} />;
      case "viewIncreaseOfShareCapitalSubscribersAllocation":
        return <SubscribersStatusDialog model={state.model} />;
      case "viewDocumentViewingStatus":
        return <ViewDocumentSentStatusDialog model={state.model} />;
      case "reopenLockedDocument":
        return <ReopenLockedDocumentDialog model={state.model} />;
      case "sendForReview":
        return <SendForReviewDialog model={state.model} />;
      case "confirmSendToBR":
        return <ConfirmSendToBRDialog model={state.model} />;
      case "confirmSendIncorporationToBR":
        return <ConfirmSendIncorporationToBRDialog model={state.model} />;
      case "incorporationSendingReceipt":
        return (
          <IncorporationSendingReceiptDialog
            onClose={closeDialog}
            confirmationDocumentCount={state.confirmationDocumentCount}
          />
        );
      case "viewSubscriptionFormBatchPreview":
        return (
          <BatchPreviewDialog
            model={state.model.documentPreviewModel}
            onClose={state.model.close}
            customBody={<SubscriptionFormBatchPreview model={state.model} />}
          />
        );
      default:
        return null;
    }
  },
);

export const SubscriptionFormBatchPreview = observer(function SummaryValidationMessages({
  model,
}: {
  model: ViewSubscriptionFormBatchPreviewModel;
}) {
  return (
    <VBox>
      {model.subscriptions.size > 0 && (
        <VBox paddingTop={"md"} maxWidth={"lg"} container>
          <Tabs
            tabs={[
              {
                id: "document",
                label: { key: "document" },
                isActive: "document" === model.selectedTab,
                onSelect: () => model.setSelectedTab("document"),
              },
              {
                id: "electronicForm",
                label: { key: "electronic-form" },
                isActive: "electronicForm" === model.selectedTab,
                onSelect: () => model.setSelectedTab("electronicForm"),
              },
            ]}
            scrollable={false}
          />
        </VBox>
      )}
      {model.isDocumentTab && <BatchPreviewReady file={model.documentPreviewModel.file} />}
      {model.isElectronicFormTab && (
        <VBox paddingTop={"md"} maxWidth={"md"} container>
          <ElectronicSubscriptionForm
            companyName={model.company.name}
            organisationNumber={model.company.organizationnumber}
            subscription={model.selectedSubscription}
          />
        </VBox>
      )}
    </VBox>
  );
});

export const SummaryValidationMessages = observer(function SummaryValidationMessages({
  decisions,
  dispatch,
  isDocumentValid,
  validationError,
  prekontrollFailure,
}: {
  decisions: {
    decisionId: string;
    title: TMessage;
    hasValidationError?: boolean;
  }[];
  dispatch: DbotEventDispatcher;
  isDocumentValid: boolean;
  validationError?: TMessage[];
  prekontrollFailure?: {
    decisionId: string;
    message: TMessage;
    isSystemUnavailable: boolean;
  };
}) {
  const { t } = useTranslation();
  const invalidDecisions = decisions.filter(
    (d) => d.hasValidationError && d.decisionId !== prekontrollFailure?.decisionId,
  );
  if (
    isEmpty(invalidDecisions) &&
    decisions.length > 0 &&
    isDocumentValid &&
    prekontrollFailure === undefined
  ) {
    return null;
  }
  return (
    <>
      {!isDocumentValid && <Alert type={"error"} message={t("dbot.document.validation.error")} />}
      {prekontrollFailure && (
        <Alert
          type={prekontrollFailure.isSystemUnavailable ? "warning" : "error"}
          message={
            <Markdown
              value={t(prekontrollFailure.message)}
              onActionClick={() =>
                dispatch([
                  { type: "dbot/open-tab", tab: "matters" },
                  {
                    type: "dbot/open-details-panel",
                    panel: { type: "decision", decisionId: prekontrollFailure.decisionId },
                  },
                ])
              }
              enableLinks
              inheritLinkColor
              disableLastMargin
            />
          }
        />
      )}
      {validationError &&
        decisions.length === 0 &&
        validationError.map((error) => <Alert key={t(error)} message={t(error)} type={"error"} />)}
      {decisions.length > 0 && invalidDecisions.length > 0 && (
        <Alert
          type={"error"}
          message={
            <ul>
              {invalidDecisions.map((d) => (
                <li key={d.decisionId}>
                  {t("dbot.status-and-next-step.error", {
                    decisionType: t(d.title),
                  })}
                </li>
              ))}
            </ul>
          }
        />
      )}
    </>
  );
});

const SignerSelection: FC<{
  model: ConfirmSendToBRDialogModel;
}> = observer(({ model }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <b>
        {t(
          model.hasMultipleTargetCompanies
            ? "dbot.summary.sendToBR.signer"
            : "dbot.summary.sendToBR.signerCount",
        )}
      </b>
      {model.hasMultipleTargetCompanies ? (
        <VBox gap={"lg"}>
          <MRadioButtonGroup model={model.signerAssignmentModel} horizontal />
          <Grid cols={2} gap={"md"} alignItems={"center"}>
            {model.signerRows.map(({ company, signerModel }) => (
              <Fragment key={company.orgNo}>
                <span>{company.name}</span>
                <MSelect
                  name={`br-signer-${company.orgNo}`}
                  model={signerModel}
                  disabled={!model.isPerCompanyEditable}
                  labelFn={(option) =>
                    `${option?.name} (${t(brSignerRoleLabel(option?.role ?? BRSignerRole.member))})`
                  }
                />
              </Fragment>
            ))}
          </Grid>
        </VBox>
      ) : (
        <VBox gap={"lg"}>
          <MRadioButtonGroup model={model.signerCountModel} horizontal />
          <VBox gap={"md"}>
            <b>
              {t(
                model.isMultipleSigners
                  ? "dbot.summary.sendToBR.selectSigners"
                  : "dbot.summary.sendToBR.selectSigner",
              )}
            </b>
            {model.isMultipleSigners ? (
              <MCheckboxGroup
                model={model.singleCompanySigners.signersModel}
                labelFn={(option) => `${option.name} (${t(brSignerRoleLabel(option.role))})`}
              />
            ) : (
              <MRadioButtonGroup
                model={model.singleCompanySigners.soleSignerModel}
                labelFn={(option) =>
                  `${option?.name} (${t(brSignerRoleLabel(option?.role ?? BRSignerRole.member))})`
                }
              />
            )}
          </VBox>
        </VBox>
      )}
      {model.additionalSigners.length > 0 && (
        <Box marginTop={"sm"}>
          <AdditionalBRSignersSection lines={model.additionalSigners} />
        </Box>
      )}
    </VBox>
  );
});

const SendToBRProgress: FC<{
  result?: Result<TMessage, TMessage>;
  title: TMessage;
  details?: TMessage[];
}> = observer(({ result, title, details = [] }) => {
  const { t } = useTranslation();
  const sending = isPending(result);
  return (
    <VBox fullheight gap={"xl"} alignItems={"center"} justifyContent={"center"}>
      <img src={sending ? envelopeSending : envelopeSent} alt={""} width={240} />
      {!sending && (
        <VBox gap={"xs"} alignItems={"center"}>
          <Box textAlign={"center"}>
            <b>{t(title)}</b>
          </Box>
          {details.map((line, i) => (
            <Box key={i} textAlign={"center"}>
              {t(line)}
            </Box>
          ))}
        </VBox>
      )}
    </VBox>
  );
});

export const ConfirmSendToBRDialog: FC<{
  model: ConfirmSendToBRDialogModel;
}> = observer(({ model }) => {
  const { t } = useTranslation();
  const hasSubmitted = isPending(model.result) || isOk(model.result);
  const companyCount = model.targetCompanies.length;
  return (
    <Dialog
      title={t("dbot.summary.sendToBR")}
      onCancel={model.onCancel}
      onSubmit={() => model.validateAndSubmit()}
      maxWidth={"md"}
      height={hasSubmitted ? "sm" : "auto"}
      submitLabel={t("dbot.summary.sendToBR.button")}
      submitResult={model.result}
      isSubmitting={isPending(model.result)}
      showAlert={!isOk(model.result)}
    >
      {hasSubmitted ? (
        <SendToBRProgress
          result={model.result}
          title={getSendToBRSuccessMessage(
            model.mattersModel.selected.length,
            companyCount,
            model.targetCompanies[0]?.name ?? "",
          )}
          details={[
            { key: "dbot.summary.sendToBR.sent.preparing" },
            { key: "dbot.summary.sendToBR.sent.noActionNeeded" },
          ]}
        />
      ) : (
        <VBox gap={"lg"} marginTop={isFailure(model.result)}>
          <div>{t("dbot.summary.sendToBR.description")}</div>
          {model.showCompanySelection && (
            <VBox>
              <b>{t("dbot.summary.sendToBR.companySelection")}</b>
              <MRadioButtonGroup model={model.companySelectionModel} horizontal />
            </VBox>
          )}
          {model.showMatters && (
            <VBox>
              <b>{t("dbot.summary.sendToBR.matters")}</b>
              <MCheckboxGroup model={model.mattersModel} />
            </VBox>
          )}
          <SignerSelection model={model} />
        </VBox>
      )}
    </Dialog>
  );
});

export const ConfirmSendIncorporationToBRDialog: FC<{
  model: ConfirmSendIncorporationToBRDialogModel;
}> = observer(({ model }) => {
  const { t } = useTranslation();
  const prekontrollPending = model.isPrekontrollPending;
  const prekontrollError = model.prekontrollError;
  const prekontrollErrors = model.prekontrollErrors;
  const hasSubmitted = isPending(model.result) || isOk(model.result);
  return (
    <Dialog
      title={t("dbot.summary.sendToBR")}
      onCancel={model.onCancel}
      onSubmit={prekontrollError ? undefined : () => model.validateAndSubmit()}
      isSubmitDisabled={!model.isSendingEnabled}
      maxWidth="md"
      height={hasSubmitted ? "sm" : "auto"}
      submitLabel={t("dbot.summary.sendToBR.button")}
      submitResult={model.result}
      isSubmitting={isPending(model.result) || prekontrollPending}
      showAlert={!isOk(model.result)}
    >
      {hasSubmitted ? (
        <SendToBRProgress
          result={model.result}
          title={{ key: "dbot.summary.sendToBR.incorporation.sent.title" }}
        />
      ) : (
        <VBox gap={"lg"} marginTop={isFailure(model.result)}>
          {prekontrollError && (
            <Alert
              type={"error"}
              icon
              message={
                <VBox gap={"sm"}>
                  <Box>{t(prekontrollError)}</Box>
                  {prekontrollErrors && prekontrollErrors.length > 0 && (
                    <VBox gap={"xs"}>
                      {prekontrollErrors.map((e, i) => (
                        <Box key={`${e.feilid ?? "x"}-${i}`}>
                          {t(dsePrekontrollErrorMessage(e))}
                          {!e.localCode && e.dynamicReturn && (
                            <>
                              {": "}
                              <code>{e.dynamicReturn}</code>
                            </>
                          )}
                        </Box>
                      ))}
                    </VBox>
                  )}
                </VBox>
              }
            />
          )}
          {!prekontrollError && (
            <VBox gap={"md"}>
              <Markdown value={t("decisionType.incorporation.br-help-text")} />
              <div>
                {t("dbot.summary.sendToBR.incorporation.consent")}
                &nbsp;
                <InfoButton
                  infoText={{ key: "dbot.summary.sendToBR.incorporation.consent.info" }}
                />
              </div>
            </VBox>
          )}
          {model.sendingDisabledReason && (
            <Alert type={"info"} icon message={model.sendingDisabledReason} />
          )}
        </VBox>
      )}
    </Dialog>
  );
});

export const IdentificationDocumentationView2 = observer(function IdentificationDocumentationView2({
  dispatch,
  item,
  attachmentType,
}: {
  dispatch: DbotEventDispatcher;
  item: DecisionListItem;
  attachmentType: AttachmentType;
}) {
  const { t } = useTranslation();
  const locale = useLanguage();
  const attachment = item.uploadedDecisionAttachments.find((a) => a.type === attachmentType);
  const followUp = item.followUps.find(
    (d) => d.data.type === FollowUpType.attachment && d.data.attachmentType === attachmentType,
  );
  const newMembers = useMemo(() => getMembers(item), [item]);
  const [editEnabled, setEditEnabled] = useState<boolean>(
    followUp !== undefined && followUp.isActive && !attachment,
  );

  const form = useMemo(
    () =>
      editEnabled && followUp
        ? new ProvideIdentificationToPersonsForm(
            dispatch,
            attachmentType,
            item.orgNo,
            item.decision.decisionId,
            followUp,
            newMembers.items,
            locale,
          )
        : null,
    [
      attachmentType,
      followUp,
      dispatch,
      editEnabled,
      item.orgNo,
      item.decision.decisionId,
      newMembers.items,
      locale,
    ],
  );

  useEffect(() => {
    if (attachment) {
      setEditEnabled(false);
    }
  }, [attachment]);

  return !followUp ? null : (
    <VBox gap={"md"}>
      <span>{t(newMembers.description)}</span>
      {form ? (
        <>
          {form.identifications.map((m, index) => (
            <VBox key={m.name} gap={"xs"}>
              <strong>{m.name}</strong>
              {m.showNationality && (
                <MSelect
                  label={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.nationality" }}
                  model={m.nationality}
                  onSelect={m.setNationality}
                />
              )}
              <MTextInput
                label={t("settings.socialSecurityNumber")}
                model={m.identityNumberModel}
                autofocus={index === 0}
              />
              {m.showDnr && (
                <MTextInput
                  label={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.dnr" }}
                  model={m.dnr}
                />
              )}
              <MTextInput label={t("email")} model={m.emailModel} />
            </VBox>
          ))}
          <Button variant={"primary"} label={t("save")} onClick={form.validateAndSave} />
        </>
      ) : attachment ? (
        <>
          <Button
            variant={"primary"}
            icon={"edit"}
            label={t("edit")}
            onClick={() => setEditEnabled(true)}
          />
          <Button
            variant={"secondary"}
            icon={"trash"}
            label={t("delete")}
            onClick={() => dispatch(item.deleteAttachment(attachment.id, followUp))}
          />
        </>
      ) : followUp.isActive ? (
        <Button variant={"primary"} label={t("add")} onClick={() => setEditEnabled(true)} />
      ) : (
        <em>{t("dbot.summary.not-ready-help-text")}</em>
      )}
    </VBox>
  );
});

const RequiredDocumentationView = observer(function RequiredDocumentationView({
  dispatch,
  item,
  attachmentType,
  followUp,
  context,
  subscribe,
}: {
  dispatch: DbotEventDispatcher;
  item: DecisionListItem;
  attachmentType: AttachmentType;
  followUp: FollowUp;
  context: ProjectContext;
  subscribe?: (listener: (event: DbotEvent) => void) => () => void;
}) {
  const { t } = useTranslation();
  const messageStore = useMessageStore();
  const attachments = item.uploadedDecisionAttachments.filter((a) => a.type === attachmentType);
  logDebug("followUp:", followUp);
  const initialState = context.contextData[item.orgNo]?.initialState;
  const form = useMemo(() => {
    return followUp && followUp.data.type === FollowUpType.attachment
      ? new AttachmentFollowUpForm(
          messageStore,
          dispatch,
          attachmentType,
          item.orgNo,
          item.decision.decisionId,
          followUp.data,
          initialState,
        )
      : null;
  }, [
    messageStore,
    attachmentType,
    followUp,
    dispatch,
    item.orgNo,
    item.decision.decisionId,
    initialState,
  ]);

  useEffect(() => {
    if (form && subscribe) {
      return subscribe(form.handleEvent);
    }
  }, [form, subscribe]);

  if (form === null) {
    return undefined;
  }

  return (
    <VBox gap={"md"}>
      {followUp.status === FollowUpStatus.COMPLETED && (
        <Tag color={"success"} label={t("completed")} />
      )}
      {when(getDecisionAttachmentsDescription(attachmentType), (data) => (
        <Markdown value={t(data)} />
      ))}
      {!followUp.isActive ? (
        <em>{t("dbot.summary.not-ready-help-text")}</em>
      ) : (
        <>
          {!item.isFollowUpLocked &&
            form.showAddAttachableDocuments(attachments, context.currentDocuments) && (
              <VBox gap={"lg"}>
                <Button
                  fullWidth
                  variant={"secondary"}
                  icon={"sparkle"}
                  label={t("dbot.summary.add-automatic-document")}
                  onClick={() =>
                    form?.addBoardStatementDocument(
                      context.currentDocuments,
                      context.documentStatuses,
                    )
                  }
                  loading={isPending(form.addAttachableDocumentsResult)}
                />
                <VBox alignItems={"center"}>{t("or")}</VBox>
              </VBox>
            )}
          {form.showAttachableDocuments(context.currentDocuments) ? (
            <VBox gap={"lg"}>
              {form.getAllAttachableDocuments(context.currentDocuments).map((documentItem) => (
                <AttachableDocumentView
                  key={documentItem.documentId}
                  dispatch={dispatch}
                  item={documentItem}
                  context={context}
                  onDelete={
                    item.isFollowUpLocked
                      ? undefined
                      : () => form.attachDocumentTypes.setValue(false)
                  }
                />
              ))}
              {!form.isAttachableDocumentsCompleted(
                context.currentDocuments,
                context.documentStatuses,
              ) && (
                <Button
                  variant={"primary"}
                  label={t("dbot.summary.go-to-document")}
                  onClick={() =>
                    dispatch({
                      type: "dbot/toggle-document-panel",
                      documentId: form.getAllAttachableDocuments(context.currentDocuments)[0]
                        .documentId,
                      expanded: true,
                    })
                  }
                />
              )}
            </VBox>
          ) : (
            <AttachmentsPanel
              fileUploadConfig={item.fileUploadConfig}
              uploadedAttachments={attachments.map((attachment) => ({
                id: attachment.id,
                filename: attachment.name,
              }))}
              onOpen={(attachmentId) => dispatch({ type: "dbot/open-attachment", attachmentId })}
              onDelete={
                item.isFollowUpLocked
                  ? undefined
                  : (attachmentId) => dispatch(item.deleteAttachment(attachmentId, followUp))
              }
              onUpload={
                item.isFollowUpLocked
                  ? undefined
                  : (files) =>
                      item.uploadNewDecisionAttachment(files, dispatch, attachmentType, followUp)
              }
              onInvalidFile={messageStore.showError}
              title={false}
            />
          )}
          {form.requiresConfirmer && (
            <VBox gap={"sm"}>
              <b>{t("dbot.summary.attachment.confirmer.heading")}</b>
              <MRadioButtonGroup
                model={form.confirmerOptionModel}
                disabled={item.isFollowUpLocked}
              />
              {form.isOtherConfirmerSelected && (
                <Autocomplete
                  label={t("dbot.summary.attachment.confirmer.search")}
                  disabled={item.isFollowUpLocked}
                  items={form.suggestedConfirmerCompanies}
                  value={form.selectedConfirmerCompany}
                  idFn={(company) => company.organizationnumber}
                  labelFn={(company) => company.name}
                  inputValue={form.confirmerSearchModel.inputValue}
                  onInputValueChange={form.setConfirmerCompanySearch}
                  onInputBlur={form.revertConfirmerCompanySearch}
                  onSelect={form.setSelectedConfirmerCompany}
                  onClear={form.clearSelectedConfirmerCompany}
                />
              )}
            </VBox>
          )}
          {form.requiresEmail && (
            <>
              <MTextInput
                label={when(getDecisionAttachmentsEmailTitle(attachmentType), t)}
                model={form.email}
                disabled={item.isFollowUpLocked}
              />
              <Alert
                type={"info"}
                message={<>{when(getDecisionAttachmentsEmailInformation(attachmentType), t)}</>}
              />
            </>
          )}
        </>
      )}
    </VBox>
  );
});

export const AttachableDocumentView = observer(function AttachableDocumentView({
  dispatch,
  item,
  context,
  onDelete,
}: {
  dispatch: DbotEventDispatcher;
  item: DocumentItem;
  context: ProjectContext;
  onDelete?: () => void;
}) {
  const { t } = useTranslation();
  const document = item.documentData as CustomDocumentData;
  const filename =
    item.documentData?.type === DocumentType.custom_document && document.customDocumentType
      ? t(customDocumentTypeLabels[document.customDocumentType]) + ".pdf"
      : t(getDocumentLabel(item.type));
  return (
    <AttachmentListItem
      attachment={{
        id: item.documentId,
        filename,
      }}
      key={item.documentId}
      onDelete={onDelete}
      onOpen={() =>
        dispatch([
          {
            type: "preview-file",
            filename,
            file: {
              url: projectApi.downloadSealedDocumentUrl({
                projectId: context.projectId,
                documentId: item.documentId,
              }),
            },
          },
        ])
      }
    />
  );
});

export const SendToBRView = observer(function SendToBRView({
  item,
  dispatch,
  context,
  followUp,
}: {
  item: DecisionListItem;
  dispatch: DbotEventDispatcher;
  context: ProjectContext;
  followUp: FollowUp;
}) {
  const { t } = useTranslation();
  const locale = useLanguage();
  const data = followUp.data as SendToBRFollowUp;
  if (!item.isEnabled) {
    return (
      <>
        <p>{t("dbot.summary.disabledSendToBR-1")}</p>
        <p>
          <span>{t("dbot.summary.disabledSendToBR-2")}</span>{" "}
          <a href={adminSettings(context.orgNo)}>{t("settings")}</a>{" "}
          <span>{t("dbot.summary.disabledSendToBR-3")}</span>
        </p>
      </>
    );
  } else if (followUp.status !== FollowUpStatus.READY) {
    return (
      <VBox gap={"lg"}>
        {data.updatedDate !== undefined && (
          <Markdown
            value={t("dbot.summary.status.updateTime", {
              date: t(relativeTime(data.updatedDate, locale)),
            })}
            enableLinks
          />
        )}
        {followUp.status === FollowUpStatus.COMPLETED && (
          <>
            {data.isRegistration ? (
              <Markdown
                value={t(
                  getDecisionApprovedInBRDescription(
                    item.decision.type,
                    data.isRegistration,
                    locale,
                    data.brPublishingDate,
                    enableSendToBRDate(item.decision.type, item.followUps),
                  ),
                )}
              />
            ) : (
              <Alert
                type="success"
                message={
                  data.brStatus === "BR_APPROVED"
                    ? t(
                        getDecisionApprovedInBRDescription(
                          item.decision.type,
                          data.isRegistration,
                          locale,
                          data.brPublishingDate,
                          enableSendToBRDate(item.decision.type, item.followUps),
                        ),
                      )
                    : t("dbot.summary.sendToBR.skipped")
                }
              />
            )}
            {data.brStatus === "BR_APPROVED" && (
              <IncorporationStatusCheckView
                item={item}
                data={data}
                context={context}
                dispatch={dispatch}
              />
            )}
          </>
        )}
        {followUp.status === FollowUpStatus.IN_PROGRESS && (
          <>
            {data.brStatus === "SENT_TO_BR" &&
              item.decision.type !== DecisionType.incorporation && (
                <>
                  <p>{t("dbot.summary.isSubmittedToBR")}</p>
                  <SelectedBRSigners
                    signers={data.brSigners}
                    additionalSigners={data.additionalSigners}
                  />
                </>
              )}
            {data.brStatus === "SENT_FOR_SIGNING" && (
              <SentForSigningView
                additionalSigners={data.additionalSigners}
                signers={data.brSigners}
                eventLogs={sortBy(item.eventLogs, (event) => event.timestamp).filter(
                  (event) => extractIsRegistration(event) === data.isRegistration,
                )}
              />
            )}
            {data.brStatus === "PROCESSING" && <p>{t("dbot.summary.isProcessingInBR")}</p>}
            {data.brStatus === "BR_APPROVED" &&
              item.decision.type !== DecisionType.incorporation && (
                <p>{t("dbot.summary.isSubmittedToBR")}</p>
              )}
            <IncorporationStatusCheckView
              item={item}
              data={data}
              context={context}
              dispatch={dispatch}
            />
          </>
        )}
      </VBox>
    );
  } else if (!followUp.isActive) {
    return (
      <>
        <Markdown
          value={t(
            getDecisionSendToBRDescription(
              item.decision.type,
              data.isRegistration,
              item?.followUps,
            ),
          )}
        />
        <p>
          <em>{t("dbot.summary.not-ready-help-text")}</em>
        </p>
      </>
    );
  }
  return (
    <VBox gap={"lg"}>
      <Markdown
        value={t(
          getDecisionSendToBRDescription(item.decision.type, data.isRegistration, item?.followUps),
        )}
      />
      <Button
        variant={"primary"}
        label={t("send")}
        onClick={(e) => {
          if (e.shiftKey && !isProductionEnvironment() && !data.isRegistration) {
            dispatch({
              type: "dbot/autoapprove-send-to-br",
              decisionId: item.decision.decisionId,
              label: { translated: "Autoapprove decision sent to BR" },
            });
          } else {
            dispatch(item.actions.confirmSendToBR(data));
          }
        }}
        loading={item.isSendingToBR}
      />
      {!data.isRegistration && item.decision.type !== DecisionType.incorporation && (
        <Box marginY={"lg"}>
          <Markdown
            value={t("dbot.summary.sendToBR.skip-this-step")}
            onActionClick={() => dispatch(item.actions.confirmSkipSendToBR)}
            enableLinks
          />
        </Box>
      )}
    </VBox>
  );
});

export const SentForSigningView = observer(function SentForSigningView({
  signers,
  additionalSigners,
  eventLogs,
}: {
  signers: BRSigner[];
  additionalSigners: AdditionalBRSigner[];
  eventLogs: EventLog[];
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"lg"}>
      <Box>{t("dbot.summary.isSentForSigningForBR")}</Box>
      <SelectedBRSigners signers={signers} additionalSigners={additionalSigners} />
      <EmailRemindersToSigners
        signerReminderEmails={eventLogs.filter(
          (log) => log.eventType === EventType.BRSignerReminderEmail,
        )}
      />
    </VBox>
  );
});

function AdditionalBRSignersSection({ lines }: { lines: AdditionalBRSignerLine[] }) {
  const { t } = useTranslation();
  return lines.length > 0 ? (
    <VBox gap={"sm"}>
      <b>{t("dbot.summary.sendToBR.additionalSigners.title")}</b>
      <span>{t("dbot.summary.sendToBR.additionalSigners.description")}</span>
      <ul className={"list-disc pl-5"}>
        {lines.map((line, i) => (
          <li key={i}>
            <Box gap={"xs"}>
              <span>{t(line.person)}</span>
              {line.companies && <TextWithTooltip {...line.companies} />}
            </Box>
          </li>
        ))}
      </ul>
    </VBox>
  ) : undefined;
}

function SelectedBRSigners({
  signers,
  additionalSigners,
}: {
  signers: BRSigner[];
  additionalSigners: AdditionalBRSigner[];
}) {
  const { t } = useTranslation();
  const additionalLines = additionalBRSignerLines(additionalSigners);
  return signers.length > 0 || additionalLines.length > 0 ? (
    <VBox gap={"sm"}>
      <strong>{t("dbot.summary.sendToBR.selected-signers")}</strong>
      <ul className={"list-disc pl-5"}>
        {signers.map((signer) => (
          <li key={brSignerId(signer)}>{t(selectedBRSignerLabel(signer))}</li>
        ))}
        {additionalLines.map((line, i) => (
          <li key={i}>{t(line.person)}</li>
        ))}
      </ul>
    </VBox>
  ) : undefined;
}

const DSE_RESPONSE_ALERT = {
  UNDER_CASE_PROCESSING: {
    type: "info",
    key: "dbot.summary.sendToBR.incorporation.checkStatus.underCaseProcessing",
  },
  APPROVED: {
    type: "success",
    key: "dbot.summary.sendToBR.incorporation.checkStatus.approved",
  },
  NOT_APPROVED: {
    type: "error",
    key: "dbot.summary.sendToBR.incorporation.checkStatus.notApproved",
  },
  REJECTED: {
    type: "error",
    key: "dbot.summary.sendToBR.incorporation.checkStatus.rejected",
  },
  OTHER: {
    type: "info",
    key: "dbot.summary.sendToBR.incorporation.checkStatus.responseAvailable",
  },
} as const;

// In the order BR walks through them: an initial control can avvise a submission before any case
// handling, a case handler can then either approve or nekte it, and the invoice follows.
const SIMULATED_RESPONSES = [
  { kind: "AVVIS", key: "dbot.summary.sendToBR.incorporation.simulate.avvis" },
  { kind: "VEDTAK", key: "dbot.summary.sendToBR.incorporation.simulate.vedtak" },
  { kind: "NEKTET", key: "dbot.summary.sendToBR.incorporation.simulate.nektet" },
  { kind: "FAKTURA", key: "dbot.summary.sendToBR.incorporation.simulate.faktura" },
] as const;

export const IncorporationStatusCheckView = observer(function IncorporationStatusCheckView({
  item,
  data,
  context,
  dispatch,
}: {
  item: DecisionListItem;
  data: SendToBRFollowUp;
  context: ProjectContext;
  dispatch: DbotEventDispatcher;
}) {
  const { t } = useTranslation();
  const locale = useLanguage();
  const ack = parseDseSubmitAck(data.brResultData);
  const result = item.incorporationStatusResult;
  const isApproved = data.brStatus === "BR_APPROVED";
  const activationLog = item.eventLogs.find(
    (log) => log.eventType === EventType.ActivatedIncorporation,
  );
  const activation =
    isApproved && activationLog ? extractActivatedIncorporation(activationLog) : undefined;
  const shouldAutoLoad = ack !== undefined && isApproved && result === undefined;
  useEffect(() => {
    if (shouldAutoLoad && !item.isCheckingIncorporationStatus) {
      dispatch(item.checkIncorporationStatusAction());
    }
  }, [shouldAutoLoad, item, dispatch]);
  if (!ack) {
    // brResultData is populated only by the DSE incorporation submit path. Without it there's
    // nothing to correlate against, so we hide the button (this is normal for Altinn flows).
    return null;
  }
  const response =
    isOk(result) && result.data.status === "RESPONSE_AVAILABLE" ? result.data.response : undefined;
  const responseAlert = DSE_RESPONSE_ALERT[response?.kind ?? "OTHER"];
  return (
    <VBox gap={"sm"}>
      {!isApproved && (
        <Button
          variant={"secondary"}
          label={t("dbot.summary.sendToBR.incorporation.checkStatus.button")}
          onClick={() => dispatch(item.checkIncorporationStatusAction())}
          loading={item.isCheckingIncorporationStatus}
        />
      )}
      {activation && (
        <IncorporationRegisteredView
          activation={activation}
          parentCompanyName={context.companies.find((c) => c.orgNo === item.orgNo)?.name ?? ""}
          dispatch={dispatch}
        />
      )}
      {isOk(result) &&
        (result.data.status === "RESPONSE_AVAILABLE" ? (
          <>
            {!(activation && response?.kind === "APPROVED") && (
              <Alert
                type={responseAlert.type}
                htmlProps={
                  response
                    ? {
                        onDoubleClick: () =>
                          dispatch([
                            {
                              type: "preview-file",
                              filename: `br-response-${response.mottakId}.xml`,
                              file: {
                                url: projectApi.dseResponseXmlUrl({
                                  projectId: context.projectId,
                                  mottakId: response.mottakId,
                                }),
                              },
                            },
                          ]),
                      }
                    : undefined
                }
                message={
                  <VBox gap={"xs"}>
                    <Markdown
                      value={t({
                        key: responseAlert.key,
                        params: {
                          status: { translated: response?.status ?? "" },
                          oppdatert: {
                            translated: formatWeekdayDateTime(locale, response?.oppdatert),
                          },
                          orgNo: response?.orgNo
                            ? { type: "org-no", value: response.orgNo }
                            : { translated: "" },
                        },
                      })}
                      inheritLinkColor
                      enableLinks
                    />
                    {/* BR names the failed control here, and there can be one line per control. */}
                    {response?.avvistsaarsak?.split("\n").map((reason, i) => (
                      <div key={i}>{reason}</div>
                    ))}
                  </VBox>
                }
              />
            )}
            {response && (response.documents ?? []).length > 0 && (
              <VBox gap={"xs"}>
                <p className={"font-semibold"}>
                  {t("dbot.summary.sendToBR.incorporation.checkStatus.documents")}
                </p>
                {(response.documents ?? []).map((doc) => (
                  <AttachmentListItem
                    key={doc.navn}
                    attachment={{ id: doc.navn, filename: doc.type ?? doc.filename }}
                    onOpen={[
                      {
                        type: "preview-file",
                        filename: doc.filename,
                        file: {
                          url: projectApi.dseVedtakDocumentUrl({
                            projectId: context.projectId,
                            navn: doc.navn,
                          }),
                        },
                      },
                    ]}
                  />
                ))}
              </VBox>
            )}
          </>
        ) : (
          <Alert
            type={"info"}
            message={t({
              key: "dbot.summary.sendToBR.incorporation.checkStatus.pending",
              params: { mottakId: { translated: ack.mottakId } },
            })}
          />
        ))}
      {isFailure(result) && <Alert type={"error"} message={t(result.error)} />}
      {!isProductionEnvironment() && (
        <Box gap={"sm"} wrap>
          {SIMULATED_RESPONSES.map(({ kind, key }) => (
            <Button
              key={kind}
              variant={"secondary"}
              label={t(key)}
              onClick={() => dispatch(item.simulateBrResponseAction(kind))}
              loading={item.isCheckingIncorporationStatus}
            />
          ))}
        </Box>
      )}
    </VBox>
  );
});

const IncorporationRegisteredView = observer(function IncorporationRegisteredView({
  activation,
  parentCompanyName,
  dispatch,
}: {
  activation: ActivatedIncorporationData;
  parentCompanyName: string;
  dispatch: DbotEventDispatcher;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"md"}>
      <div>
        <p className={"font-semibold"}>
          {t("dbot.summary.sendToBR.incorporation.registered.header", {
            companyName: activation.companyName,
          })}
        </p>
        <Markdown
          value={t("dbot.summary.sendToBR.incorporation.registered.description", {
            orgNo: { type: "org-no", value: activation.newOrgNo },
          })}
          disableLastMargin
        />
      </div>
      <ul className={"list-disc pl-5"}>
        <li>{t("dbot.summary.sendToBR.incorporation.registered.company-data")}</li>
        {activation.adminNames.length > 0 && (
          <li>
            {t("dbot.summary.sendToBR.incorporation.registered.admins", {
              parentCompany: parentCompanyName,
              admins: { type: "list", value: activation.adminNames },
            })}
          </li>
        )}
        {activation.productName && (
          <li>
            {activation.enterpriseAccountName
              ? t("dbot.summary.sendToBR.incorporation.registered.subscription-in-account", {
                  accountName: activation.enterpriseAccountName,
                  subscription: t(productNameLabel(activation.productName)),
                })
              : t("dbot.summary.sendToBR.incorporation.registered.subscription", {
                  subscription: t(productNameLabel(activation.productName)),
                })}
          </li>
        )}
      </ul>
      <Button
        variant={"primary"}
        arrow={"out"}
        fullWidth
        label={t("dbot.summary.sendToBR.incorporation.registered.goto", {
          companyName: activation.companyName,
        })}
        onClick={() => dispatch(navigate(adminDashboard(activation.newOrgNo), { newWindow: true }))}
      />
    </VBox>
  );
});

export const EmailRemindersToSigners = observer(function EmailRemindersToSigners({
  signerReminderEmails,
}: {
  signerReminderEmails: EventLog[];
}) {
  const { t } = useTranslation();
  const locale = useLanguage();

  return signerReminderEmails.length > 0 ? (
    <VBox gap={false}>
      <Box>
        <Markdown value={t("dbot.summary.isSentForSigningForBR.reminders")} />
      </Box>
      {signerReminderEmails.map((log) => (
        <Grid cols={3} gap={false} key={log.id}>
          <GridItem colSpan={1}>{formatDateTime(locale, log.timestamp)}</GridItem>
          <GridItem colSpan={2}>
            {t("dbot.summary.isSentForSigningForBR.reminder-sent-to", {
              signers: {
                type: "list",
                value: extractBRSignerReminders(log).map((signer) =>
                  signer.name ? `${signer.name} (${signer.email})` : signer.email,
                ),
              },
            })}
          </GridItem>
        </Grid>
      ))}
    </VBox>
  ) : undefined;
});

export const ApplyToSystemView = observer(function ApplyToSystemView({
  item,
  dispatch,
  context,
}: {
  item: DecisionListItem;
  dispatch: DbotEventDispatcher;
  context: ProjectContext;
}) {
  const { t } = useTranslation();
  const followUp = item.followUps.find((d) => d.data.type === FollowUpType.apply);
  const brFollowUp = item.followUps.find((d) => d.data.type === FollowUpType.sendToBR);

  if (!followUp) return null;
  else if (!item.isEnabled) {
    return (
      <>
        <p>{t("dbot.summary.disabledApply-1")}</p>
        <p>
          <span>{t("dbot.summary.disabledApply-2")}</span>{" "}
          <a href={adminSettings(context.orgNo)}>{t("settings")}</a>{" "}
          <span>{t("dbot.summary.disabledApply-3")}</span>
        </p>
      </>
    );
  } else if (item.applied) {
    if (item.decision.type === DecisionType.incorporation) {
      return <IncorporationActivatedView item={item} dispatch={dispatch} />;
    }
    return (
      <>
        <p>
          <Icon name={"check"} color={"success"} /> {t("dbot.summary.appliedDecisionToSystem")}
        </p>
      </>
    );
  } else if (!followUp.isActive) {
    return (
      <>
        <p>{t("dbot.summary.applyDecisionToSystem.tooltip")}</p>
        <p>
          <em>{t("dbot.summary.not-ready-help-text")}</em>
        </p>
      </>
    );
  } else if (!item.isPreviousDecisionApplied) {
    return <p>{t("dbot.summary.disabledApplyPreviousDecision")}</p>;
  } else if (item.decision.type === DecisionType.incorporation) {
    return <ActivateIncorporationView item={item} dispatch={dispatch} context={context} />;
  }
  return (
    <>
      <p>{t("dbot.summary.applyDecisionToSystem.tooltip")}</p>
      {followUp.data.type === "apply" &&
        brFollowUp?.status !== FollowUpStatus.COMPLETED &&
        followUp.data.isRightsFromSubscription && (
          <Alert type={"info"} message={{ key: "dbot.summary.isRightsFromSubscription" }} />
        )}
      {followUp.data.type === "apply" &&
        brFollowUp?.status !== FollowUpStatus.COMPLETED &&
        !followUp.data.isRightsFromSubscription &&
        item.decision.type === DecisionType.increaseOfShareCapital && (
          <Alert
            type={"info"}
            message={{ key: "dbot.summary.isShareCapitalIncreaseWithoutRights" }}
          />
        )}
      <Button
        variant={"primary"}
        label={t("register")}
        onClick={() => {
          if (
            followUp.data.type === "apply" &&
            brFollowUp?.status !== FollowUpStatus.COMPLETED &&
            item.decision.type === DecisionType.increaseOfShareCapital
          ) {
            dispatch(
              confirmAction({
                text: { key: "dbot.summary.shareCapitalIncrease.confirm" },
                confirmLabel: { key: "yes" },
                cancelLabel: { key: "no" },
                onConfirm: [thunk(() => dispatch(item.actions.applyDecision))],
              }),
            );
          } else dispatch(item.actions.applyDecision);
        }}
        loading={item.isApplying}
      />
    </>
  );
});

const SubmitToRegisterOfCompanyAccountsView = observer(
  function SubmitToRegisterOfCompanyAccountsView({
    item,
    dispatch,
    followUp,
  }: {
    item: DecisionListItem;
    dispatch: DbotEventDispatcher;
    followUp: FollowUp;
  }) {
    const { t } = useTranslation();
    const locale = useLanguage();
    const completed = followUp.status === FollowUpStatus.COMPLETED;
    const altinn = getInterimBalanceSheetAltinnLink(locale);

    return (
      <VBox gap={"lg"}>
        <Markdown
          enableLinks
          value={t("dbot.follow-up.submitToRegisterOfCompanyAccounts.description", {
            link: altinn.url,
          })}
        />
        <Button
          variant={"primary"}
          arrow={"out"}
          label={t(altinn.label)}
          onClick={() => dispatch({ type: "load-url", url: altinn.url, newWindow: true })}
        />
        {!completed && (
          <Button
            variant={"secondary"}
            label={t("dbot.follow-up.submitToRegisterOfCompanyAccounts.flagAsCompleted")}
            onClick={() =>
              dispatch(
                updateFollowUpStatusEvent(item.orgNo, item.decision.decisionId, {
                  type: FollowUpType.submitToRegisterOfCompanyAccounts,
                  isSubmitted: true,
                }),
              )
            }
          />
        )}
      </VBox>
    );
  },
);

const IncorporationActivatedView = observer(function IncorporationActivatedView({
  item,
  dispatch,
}: {
  item: DecisionListItem;
  dispatch: DbotEventDispatcher;
}) {
  const { t } = useTranslation();
  const log = item.eventLogs.find((l) => l.eventType === EventType.ActivatedIncorporation);
  const activation = log && extractActivatedIncorporation(log);
  if (!log || !activation) {
    return (
      <p>
        <Icon name={"check"} color={"success"} /> {t("dbot.summary.appliedDecisionToSystem")}
      </p>
    );
  }
  return (
    <VBox gap={"md"}>
      <div>
        <p className={"font-semibold"}>{t("dbot.summary.completed")}</p>
        <p>
          {t("dbot.summary.activateIncorporation.completed.description", {
            companyName: activation.companyName,
            at: { type: "datetime", value: log.timestamp, withoutPrefix: true },
            userName: log.actor.name,
          })}
        </p>
      </div>
      <div>
        <p>{t("dbot.summary.activateIncorporation.completed.confirm.header")}</p>
        <ul className={"list-disc pl-5"}>
          <li>{t("dbot.summary.activateIncorporation.completed.confirm.shareholder-register")}</li>
          <li>{t("dbot.summary.activateIncorporation.completed.confirm.articles")}</li>
          <li>{t("dbot.summary.activateIncorporation.completed.confirm.roles")}</li>
        </ul>
      </div>
      <Button
        variant={"secondary"}
        arrow={"out"}
        fullWidth
        label={t("dbot.summary.activateIncorporation.completed.goto", {
          companyName: activation.companyName,
        })}
        onClick={() =>
          dispatch([navigate(adminDashboard(activation.newOrgNo), { newWindow: true })])
        }
      />
    </VBox>
  );
});

const activationProducts: ProductName[] = ["digital_company", "shareholder_register"];

const ActivateIncorporationView = observer(function ActivateIncorporationView({
  item,
  dispatch,
  context,
}: {
  item: DecisionListItem;
  dispatch: DbotEventDispatcher;
  context: ProjectContext;
}) {
  const { t } = useTranslation();
  const activationInfo = item.activationInfo;
  useEffect(() => {
    if (activationInfo === undefined) {
      dispatch(item.fetchActivationInfoAction());
    }
  }, [activationInfo, item, dispatch]);

  const decisionData = item.decision.decisionData;
  const incorporation =
    decisionData?.type === DecisionType.incorporation ? decisionData : undefined;
  const companyName = incorporation?.companyNames[0] ?? "";
  const parentCompanyName = context.companies.find((c) => c.orgNo === item.orgNo)?.name ?? "";
  const admins = isOk(activationInfo) ? activationInfo.data.admins : [];
  const enterpriseAccounts = isOk(activationInfo) ? activationInfo.data.enterpriseAccounts : [];
  const subscription = item.activationSubscription ?? defaultIncorporationActivationSubscription;
  const selectedAccount =
    enterpriseAccounts.find((a) => a.id === subscription.enterpriseAccountId) ?? null;

  return (
    <VBox gap={"md"}>
      <div>
        <p className={"font-semibold"}>{t("dbot.summary.activateIncorporation.ready.header")}</p>
        <p>{t("dbot.summary.activateIncorporation.ready.description", { companyName })}</p>
      </div>
      <div>
        <p>{t("dbot.summary.activateIncorporation.effects.header")}</p>
        <ul className={"list-disc pl-5"}>
          <li>
            <Markdown
              value={t("dbot.summary.activateIncorporation.effects.terms")}
              enableLinks
              disableLastMargin
            />
          </li>
          <li>
            {incorporation?.incorporationDate
              ? t("dbot.summary.activateIncorporation.effects.transaction", {
                  date: { type: "date", value: incorporation.incorporationDate },
                })
              : t("dbot.summary.activateIncorporation.effects.transaction-without-date")}
          </li>
          <li>
            {t("dbot.summary.activateIncorporation.effects.admins", {
              parentCompany: parentCompanyName,
              companyName,
            })}
            {admins.length > 0 && (
              <ul className={"list-disc pl-5"}>
                {admins.map((admin) => (
                  <li key={admin.id}>{admin.name}</li>
                ))}
              </ul>
            )}
          </li>
        </ul>
      </div>
      <p className={"font-semibold"}>
        {t("dbot.summary.activateIncorporation.subscription.header")}
      </p>
      {enterpriseAccounts.length > 0 && (
        <Select
          label={t("terms-of-service.enterprise-account.label")}
          value={selectedAccount}
          items={[null, ...enterpriseAccounts]}
          idFn={(account) => account?.id ?? "none"}
          labelFn={(account) =>
            account ? account.name : t("terms-of-service.enterprise-account.none")
          }
          onSelect={(account) =>
            dispatch(
              item.selectActivationSubscriptionAction({
                ...subscription,
                enterpriseAccountId: account?.id,
              }),
            )
          }
          fullWidth
        />
      )}
      <Select
        label={t("subscription")}
        value={subscription.productName}
        items={activationProducts}
        idFn={(product) => product}
        labelFn={(product) => productNameLabel(product)}
        onSelect={(product) =>
          dispatch(
            item.selectActivationSubscriptionAction({
              ...subscription,
              productName: product,
            }),
          )
        }
        fullWidth
      />
      <Button
        variant={"primary"}
        label={t("dbot.summary.activateIncorporation.button", { companyName })}
        onClick={() => dispatch(item.actions.applyDecision)}
        loading={item.isApplying}
        fullWidth
      />
    </VBox>
  );
});

const RegisterInShareholderRegisterView = observer(function RegisterInShareholderRegisterView({
  item,
  dispatch,
  followUp,
}: {
  item: DecisionListItem;
  dispatch: DbotEventDispatcher;
  followUp: FollowUp;
}) {
  const { t } = useTranslation();
  const completed = followUp.status === FollowUpStatus.COMPLETED;
  const log = item.eventLogs.find(
    (l) => l.eventType === EventType.RegisteredShareTransferInShareholderRegister,
  );
  const transfer = log && extractRegisteredShareTransfer(log);

  return (
    <VBox gap={"lg"}>
      <Markdown value={t("dbot.follow-up.registerInShareholderRegister.description")} />
      {completed ? (
        <p>
          <Icon name={"check"} color={"success"} />{" "}
          {log && transfer
            ? t("dbot.follow-up.registerInShareholderRegister.completed", {
                numberOfShares: { type: "number", value: transfer.numberOfShares },
                transferor: transfer.transferorName,
                acquirer: transfer.acquirerName,
                userName: log.actor.name,
                at: { type: "datetime", value: log.timestamp, withoutPrefix: true },
              })
            : t("dbot.follow-up.registerInShareholderRegister.completedWithoutDetails")}
        </p>
      ) : (
        <Button
          variant={"secondary"}
          label={t("dbot.follow-up.registerInShareholderRegister.flagAsCompleted")}
          onClick={() =>
            dispatch(
              updateFollowUpStatusEvent(item.orgNo, item.decision.decisionId, {
                type: FollowUpType.registerInShareholderRegister,
                isRegistered: true,
              }),
            )
          }
        />
      )}
    </VBox>
  );
});

export const FollowUpDecisionDetailsPanel = observer(function FollowUpDecisionDetailsPanel({
  panel,
  item,
  dispatch,
  context,
  subscribe,
}: {
  panel: FollowUpDetailsPanel;
  item: DecisionListItem;
  dispatch: DbotEventDispatcher;
  context: ProjectContext;
  subscribe?: (listener: (event: DbotEvent) => void) => () => void;
}) {
  const locale = useLanguage();
  const panels = getDecisionFollowUpPanels(item, locale);
  const panelData = panels.find((p) => {
    switch (panel.followUpType) {
      case FollowUpType.apply:
      case FollowUpType.mergerCounterpartResolution:
      case FollowUpType.submitToRegisterOfCompanyAccounts:
      case FollowUpType.registerInShareholderRegister:
        return p.followUp?.item.data.type === panel.followUpType;
      case FollowUpType.sendToBR:
        return (
          p.followUp?.item.data.type === panel.followUpType &&
          p.followUp.isBRRegistration === panel.isBRRegistration
        );
      case FollowUpType.attachment: {
        return p.followUp?.item.data.type === panel.followUpType && p.id === panel.attachmentType;
      }
    }
  });
  const followUp = panelData?.followUp?.item;

  function render() {
    switch (followUp?.data.type) {
      case FollowUpType.attachment: {
        switch (followUp.data.attachmentType) {
          case AttachmentType.BOARD_MEMBERS_ELECTION_IDENTIFICATIONS:
          case AttachmentType.PROCURA_BY_NAME_OTHERS_IDENTIFICATION:
          case AttachmentType.EMPLOY_CEO_IDENTIFICATION:
            return (
              <IdentificationDocumentationView2
                dispatch={dispatch}
                item={item}
                attachmentType={followUp.data.attachmentType}
              />
            );
          default:
            return (
              <RequiredDocumentationView
                dispatch={dispatch}
                item={item}
                attachmentType={followUp.data.attachmentType}
                followUp={followUp}
                context={context}
                subscribe={subscribe}
              />
            );
        }
      }
      case FollowUpType.sendToBR:
        return (
          <SendToBRView item={item} dispatch={dispatch} context={context} followUp={followUp} />
        );
      case FollowUpType.apply:
        return <ApplyToSystemView item={item} dispatch={dispatch} context={context} />;
      case FollowUpType.mergerCounterpartResolution:
        return (
          <MergerCounterpartResolutionView item={item} dispatch={dispatch} followUp={followUp} />
        );
      case FollowUpType.submitToRegisterOfCompanyAccounts:
        return (
          <SubmitToRegisterOfCompanyAccountsView
            item={item}
            dispatch={dispatch}
            followUp={followUp}
          />
        );
      case FollowUpType.registerInShareholderRegister:
        return (
          <RegisterInShareholderRegisterView item={item} dispatch={dispatch} followUp={followUp} />
        );
      default:
        return null;
    }
  }

  return (
    <EditDetailsPanel
      dispatch={dispatch}
      title={{ key: "dbot.follow-up" }}
      heading={panelData?.heading}
      content={render}
      next={getOpenNextPanelEvent(panelData?.id ?? "", panels)}
      prev={getOpenPrevPanelEvent(panelData?.id ?? "", panels)}
      toggleMaximized={{ type: "dbot/toggle-maximize-details-panel" }}
      isMaximized={context.detailsPanelMaximized}
    />
  );
});

export const FollowUpDecisionSummary = observer(function FollowUpDecisionSummary({
  item,
  companies,
  includeCompanyName,
}: {
  item: DecisionListItem;
  companies: ProjectCompany[];
  includeCompanyName?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Box alignItems="center">
      <span className={"font-semibold"}>{t(getDecisionName(item.decision.type))}</span>
      {includeCompanyName && (
        <span className={"subtle"}>{companies.find((c) => c.orgNo === item.orgNo)?.name}</span>
      )}
    </Box>
  );
});

export const FollowUpDecision = observer(function FinishDecision({
  item,
  dispatch,
}: {
  item: DecisionListItem;
  dispatch: DbotEventDispatcher;
}) {
  const locale = useLanguage();
  const { t } = useTranslation();
  return (
    <VBox>
      <DetailsSummaryList
        dispatch={dispatch}
        isLocked={false}
        panels={getDecisionFollowUpPanels(item, locale)}
      />
      {!item.supportsSendToBR && (
        <small className={"subtle"}>{t("dbot.summary.sendToBR.notSupported")}</small>
      )}
    </VBox>
  );
});

const MergerCounterpartResolutionView = observer(function MergerCounterpartResolutionView({
  item,
  dispatch,
  followUp,
}: {
  item: DecisionListItem;
  dispatch: DbotEventDispatcher;
  followUp: FollowUp;
}) {
  const { t } = useTranslation();
  const decisionData = item.decision.decisionData;
  const merger = decisionData?.type === DecisionType.merger ? decisionData : undefined;
  const counterparty = merger ? counterpartyOf(merger) : undefined;
  if (merger === undefined || counterparty === undefined) {
    return null;
  }
  const counterpartProjectId = merger.counterpartProjectId;
  return (
    <VBox gap={"sm"}>
      {counterpartProjectId === undefined ? (
        <>
          <span>
            {t(
              merger.mergerType === MergerDecisionType.mergerBetweenParentAndWhollyOwnedSubsidiary
                ? "dbot.summary.mergerCounterpart.description-board-only"
                : "dbot.summary.mergerCounterpart.description",
              { companyName: counterparty.name },
            )}
          </span>
          <div>
            <Button
              variant={"secondary"}
              label={t("dbot.summary.mergerCounterpart.create", {
                companyName: counterparty.name,
              })}
              loading={item.isCreatingMergerCounterpart}
              onClick={() => dispatch(createMergerCounterpartEvent(item.decision.decisionId))}
            />
          </div>
        </>
      ) : (
        <>
          {followUp.status !== FollowUpStatus.COMPLETED && (
            <span>
              {t("dbot.summary.mergerCounterpart.waiting", { companyName: counterparty.name })}
            </span>
          )}
          <div>
            <LinkButton
              label={t("dbot.summary.mergerCounterpart.open-project")}
              onClick={() =>
                dispatch(openCounterpartProjectEvent(counterparty.orgNo, counterpartProjectId))
              }
            />
          </div>
        </>
      )}
    </VBox>
  );
});
